import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { isEqual } from 'lodash-es';
import { debounceTime, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import { phoneMask } from '../../../../../constants/maskito.constants';
import { checkFormValidity } from '../../../../../helpers/form';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import { GenericComponent } from '../../../../components/generic/generic.component';

interface FormModel {
  userInformationCompany: FormControl<string | null>;
  userInformationEmail: FormControl<string | null>;
  userInformationFirstName: FormControl<string | null>;
  userInformationLastName: FormControl<string | null>;
  userInformationPhone: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: UserInformationComponent
    }
  ],
  selector: 'red-net-sheet-user-information',
  standalone: false,
  templateUrl: './user-information.component.html'
})
export class UserInformationComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);

  form = new FormGroup<FormModel>({
    userInformationCompany: new FormControl<string | null>(null),
    userInformationEmail: new FormControl<string | null>(null, {
      validators: [Validators.email]
    }),
    userInformationFirstName: new FormControl<string | null>(null),
    userInformationLastName: new FormControl<string | null>(null),
    userInformationPhone: new FormControl<string | null>(null, {
      validators: [Validators.minLength(12), Validators.maxLength(12)]
    })
  });

  constructor() {
    super();

    this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged((previous, current) => isEqual(previous, current))
      )
      .subscribe((values) => {
        this.store.dispatch(
          fromNetSheetActions.updateUserInformation({
            payload: values
          })
        );
      });
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectUserInformationFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(
            {
              ...data
            },
            { emitEvent: false }
          );
        })
    );
  }

  readonly phoneMask: MaskitoOptions = phoneMask;
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  readonly valid = checkFormValidity(this.form);
}
